/**
 * @file PopulationGroupList.js
 * @description Displays a list of custom population groups with functionality to manage them.
 */

import React, { useState, useContext, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './PopulationGroup.module.scss';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import Toast from '../ui/toasts/Toast';
import style from './PopulationGroup.module.scss';
import { formatDate, formatName, getNameForDuplicate } from '../../utils/utils';
import { SecurityAttributes, LogEventType } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import { useAuthContext } from '../../store/Contexts';
import {
    fetchPopulationGroupByID,
    addGroup,
    deleteGroups,
    updateGroup,
    searchPopulationGroups,
} from '../../services/populationGroupService';
import Spinner from '../ui/spinners/Spinner';
import PaginationComponent from '../helpers/PaginationComponent';
import SearchComponent from '../helpers/search/SearchComponent';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import PopulationGroupDownloadMenu from './PopulationGroupDownloadMenu';
import { getText } from '../../i18n';

const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const ITEMS_PER_PAGE = 20;

const PopulationGroupList = ({ onAddEditGroup, onViewIndividuals }) => {
    const { showModal } = useContext(ModalContext);
    const [groups, setGroups] = useState([]);
    const [selectedGroupIds, setSelectedGroupIds] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        key: 'name',
        direction: SortDirections.ASCENDING,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const selectAllCheckboxRef = useRef(null);
    const { hasAttribute } = useAuthContext();
    const [searchTerm, setSearchTerm] = useState('');
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    useEffect(() => {
        loadGroups();
    }, [currentPage]);

    useEffect(() => {
        if (!initialLoadComplete) return;

        const timeoutId = setTimeout(() => {
            loadGroups();
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, sortConfig]);

    const loadGroups = async () => {
        setIsLoading(true);
        try {
            const response = await searchPopulationGroups(
                null,
                currentPage,
                ITEMS_PER_PAGE,
                true,
                false,
                false,
                searchTerm,
                sortConfig.key + sortConfig.direction,
            );
            setGroups(response.populationGroups);
            setTotalPages(Math.ceil(response.recordCount / ITEMS_PER_PAGE));
            setInitialLoadComplete(true);
        } catch (e) {
            Toast.error(getText('msg_f2dd9dd0b1b9'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteGroups = async groupIds => {
        const successfulDeletions = [];
        const failedDeletions = [];

        for (const groupId of groupIds) {
            try {
                await deleteGroups(groupId);
                successfulDeletions.push(groupId);
            } catch (error) {
                failedDeletions.push(groupId);
                console.error(`Failed to delete group ${groupId}:`, error);
            }
        }

        if (successfulDeletions.length && failedDeletions.length) {
            Toast.error(getText('msg_f9597be47f76'));
        }

        if (successfulDeletions.length && !failedDeletions.length) {
            Toast.success(getText('msg_e02e0546f432'));
        }

        loadGroups();
    };

    const handleCopyGroups = async groupIds => {
        setIsLoading(true);

        for (let groupId of groupIds) {
            try {
                const originalGroupData =
                    await fetchPopulationGroupByID(groupId);

                const existingNames = groups.map(group => group.name);
                const newName = getNameForDuplicate(
                    originalGroupData.name,
                    existingNames,
                );

                delete originalGroupData.id;
                originalGroupData.name = newName;

                originalGroupData.segments.map(e => {
                    delete e.id;
                    return { ...e };
                });

                const groupCopy = { ...originalGroupData };
                await addGroup(groupCopy);
                Toast.success(
                    `Group "${originalGroupData.name}" copied successfully`,
                );
            } catch (error) {
                Toast.error(
                    error?.response?.data?.detail ||
                        'Failed to copy group: Unknown error',
                );
            }
        }
        loadGroups();
    };

    const handleToggleGroupStatus = async groupId => {
        try {
            const group = await fetchPopulationGroupByID(groupId);

            if (!group) throw new Error('Group not found');

            const updatedGroup = { ...group, isActive: !group.isActive };

            await updateGroup(updatedGroup);
            Toast.success(
                `Group ${group.isActive ? 'disabled' : 'enabled'} successfully`,
            );
            loadGroups();
        } catch (error) {
            console.error('Failed to toggle group status', error);
            Toast.error(
                error?.response?.data?.detail ||
                    'Failed to toggle group status',
            );
        }
    };

    useEffect(() => {
        if (selectAllCheckboxRef.current) {
            selectAllCheckboxRef.current.indeterminate =
                selectedGroupIds.length > 0 &&
                selectedGroupIds.length < groups.length;
        }
    }, [selectedGroupIds, groups]);

    const handleSort = key => {
        setSortConfig({
            key,
            direction:
                sortConfig.key === key &&
                sortConfig.direction === SortDirections.ASCENDING
                    ? SortDirections.DESCENDING
                    : SortDirections.ASCENDING,
        });
    };

    const renderSortArrow = key => {
        if (sortConfig.key !== key) {
            return null;
        }
        return (
            <span className={`px-2 ${style.sortArrowIcon}`}>
                <svg
                    width="11"
                    height="6"
                    viewBox="0 0 11 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d={
                            sortConfig.direction === SortDirections.ASCENDING
                                ? 'M1 5.25L5.25 0.75L9.5 5.25'
                                : 'M1 0.75L5.25 5.25L9.5 0.75'
                        }
                        stroke="#2F2B43"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
        );
    };

    const handleCheckboxChange = (groupId, isChecked) => {
        setSelectedGroupIds(
            isChecked
                ? [...selectedGroupIds, groupId]
                : selectedGroupIds.filter(id => id !== groupId),
        );
    };

    const handleSelectAll = isChecked => {
        setSelectedGroupIds(isChecked ? groups.map(group => group.id) : []);
    };

    const handleDelete = () => {
        handleDeleteGroups(selectedGroupIds);
        setSelectedGroupIds([]);
    };

    const isGroupSelected = id => selectedGroupIds.includes(id);

    const openDeleteConfirmationModal = () => {
        if (!selectedGroupIds.length) {
            Toast.error(getText('msg_c8ccc5b97e0d'));
            return;
        }

        showModal({
            type: ModalTypes.DELETE_CONFIRMATION,
            title: getText('msg_b957efeb6067'),
            subtitle: getText('msg_ebaf1ef433b9'),
            onYes: () => handleDelete(),
            onNo: () => {},
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_f6fdbe48dc54'),
        });
        logEvent(
            LogEventType.DeleteClicked,
            `Population groups delete clicked`,
        );
    };

    const duplicateGroup = () => {
        if (!selectedGroupIds.length) {
            Toast.error(getText('msg_b244a580f588'));
            return;
        }
        handleCopyGroups(selectedGroupIds);
        setSelectedGroupIds([]);
    };
    const handleSearch = term => {
        setSearchTerm(term);
    };

    return (
        <>
            <div className="row mb-4">
                <div className="col d-flex justify-content-start">
                    <div className="ms-2">
                        <SearchComponent
                            onSearch={event => handleSearch(event)}
                            placeholder={getText('msg_f90a80f7e40f')}
                            initialValue={searchTerm}
                        />
                    </div>
                </div>
                {!hasAttribute(SecurityAttributes.PopulationReadOnly) && (
                    <div className="col d-flex justify-content-end">
                        <div className="d-flex align-items-center">
                            <span
                                type="button"
                                onClick={openDeleteConfirmationModal}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-trash3 me-2" />
                                {getText('msg_f6fdbe48dc54')}
                            </span>
                            <span
                                type="button"
                                onClick={duplicateGroup}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-copy me-2" />
                                {getText('msg_972d57379db3')}
                            </span>
                            <div to="new-portal-page">
                                <span
                                    onClick={() => onAddEditGroup('')}
                                    type="button"
                                    className="ms-3 me-4  button-base ing-button-primary-left "
                                >
                                    <i className="bi bi-plus-lg me-1" />
                                    {getText('msg_61cc55aa0453')}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="table-responsive-xxl w-100">
                <table className="table table-hover align-middle">
                    <thead className="custom-table-header table-light">
                        <tr>
                            <th
                                className={`text-center ${style.checkboxColumnHead}`}
                            >
                                <input
                                    type="checkbox"
                                    className={`form-check-input`}
                                    ref={selectAllCheckboxRef}
                                    onChange={e =>
                                        handleSelectAll(e.target.checked)
                                    }
                                    checked={
                                        selectedGroupIds.length ===
                                        groups.length
                                    }
                                />
                            </th>
                            <th
                                className={style.columnHead}
                                onClick={() => handleSort('name')}
                            >
                                <span className="table-column-header-text">
                                    {getText('msg_709a23220f2c')}{' '}
                                    {renderSortArrow('name')}
                                </span>
                            </th>
                            <th
                                className={`${style.columnHead} ${style.subTextColumn}`}
                                onClick={() => handleSort('lastEdited')}
                            >
                                <span className="table-column-header-text">
                                    {getText('msg_0c79adcf41ec')}{' '}
                                    {renderSortArrow('lastEdited')}
                                </span>
                            </th>
                            <th
                                className={`${style.columnHead} ${style.subTextColumn}`}
                                onClick={() => handleSort('lastEditedBy')}
                            >
                                <span className="table-column-header-text">
                                    {getText('msg_ed8e9c116b19')}{' '}
                                    {renderSortArrow('lastEditedBy')}
                                </span>
                            </th>
                            <th
                                className={style.columnHead}
                                onClick={() => handleSort('isActive')}
                            >
                                <span className="table-column-header-text">
                                    {getText('msg_20063ad90532')}{' '}
                                    {renderSortArrow('isActive')}
                                </span>
                            </th>
                            <th
                                className={`${style.columnHead} ${style.actionsColumn}`}
                            >
                                <span className="table-column-header-text">
                                    {getText('msg_c3cd636a585b')}
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {!isLoading && groups.length === 0 && (
                            <tr>
                                <td className="text-center" colSpan={6}>
                                    <NotFoundBox
                                        backgroundColor="#f0f4ff"
                                        title={getText('msg_aca14ec90fb5')}
                                        description={getText(
                                            'msg_12987c465e21',
                                        )}
                                    />
                                </td>
                            </tr>
                        )}
                        {isLoading ? (
                            <tr>
                                <td className="text-center" colSpan={6}>
                                    <Spinner
                                        width={50}
                                        className={styles.loadingSpinner}
                                    />
                                </td>
                            </tr>
                        ) : (
                            groups.map(group => (
                                <tr key={group.id}>
                                    <td className="text-center">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={isGroupSelected(group.id)}
                                            onChange={e =>
                                                handleCheckboxChange(
                                                    group.id,
                                                    e.target.checked,
                                                )
                                            }
                                        />
                                    </td>
                                    <td
                                        className="item-page-name titleCell"
                                        onClick={() => onAddEditGroup(group.id)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={e => {
                                            if (
                                                e.key === 'Enter' ||
                                                e.key === ' '
                                            ) {
                                                onAddEditGroup(group.id);
                                            }
                                        }}
                                    >
                                        {group.name}
                                    </td>
                                    <td className={style.subTextColumn}>
                                        {formatDate(group.lastEdited)}
                                    </td>
                                    <td className={style.subTextColumn}>
                                        {formatName(group.lastEditedBy)}
                                    </td>
                                    <td>
                                        <div className="form-switch">
                                            <input
                                                className="form-check-input custom-switch-input ml-3"
                                                type="checkbox"
                                                checked={group.isActive}
                                                onChange={() =>
                                                    handleToggleGroupStatus(
                                                        group.id,
                                                    )
                                                }
                                            />
                                        </div>
                                    </td>
                                    <td className={styles.actionsColumn}>
                                        <div className={styles.actionsCell}>
                                            <PopulationGroupDownloadMenu
                                                populationGroup={group}
                                            />
                                            <span
                                                className={styles.viewButton}
                                                onClick={() => {
                                                    onViewIndividuals(group);
                                                }}
                                            >
                                                <i className="bi bi-eye"></i>
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            {groups.length > 0 && (
                <PaginationComponent
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            )}
        </>
    );
};

PopulationGroupList.propTypes = {
    onAddEditGroup: PropTypes.func.isRequired,
    onViewIndividuals: PropTypes.func.isRequired,
};

export default PopulationGroupList;
